// Regras de CPF/CNPJ do sistema, incluindo o CNPJ alfanumérico da Receita Federal
// (IN RFB 2.229/2024): 12 posições com letras A-Z ou números + 2 dígitos verificadores
// numéricos. CNPJs só com números continuam válidos e idênticos. Letras sempre
// maiúsculas. CPF continua só com números.

const PESOS_CNPJ = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

// Mantém só dígitos.
const somenteDigitos = (valor) => String(valor ?? "").replace(/\D/g, "");

// Mantém letras (maiúsculas) e números, sem limite de tamanho.
const somenteAlfanumericos = (valor) =>
  String(valor ?? "")
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");

// CNPJ limpo: até 12 letras/números + até 2 dígitos verificadores (só números).
const limparCnpj = (valor) => {
  let saida = "";
  for (const c of somenteAlfanumericos(valor)) {
    if (saida.length >= 14) break;
    if (saida.length < 12 || /\d/.test(c)) saida += c;
  }
  return saida;
};

// Campo que aceita CPF ou CNPJ: com letra é CNPJ; só números, até 14 dígitos.
const limparCpfCnpj = (valor) => {
  const limpo = somenteAlfanumericos(valor);
  return /[A-Z]/.test(limpo) ? limparCnpj(limpo) : limpo.slice(0, 14);
};

// Enquanto digita: tem letra ou passou de 11 caracteres => está digitando CNPJ.
const pareceCnpj = (valor) => {
  const limpo = limparCpfCnpj(valor);
  return /[A-Z]/.test(limpo) || limpo.length > 11;
};

// "CPF" (11 dígitos), "CNPJ" (14 posições) ou "" (incompleto/inválido).
const tipoDocumento = (valor) => {
  const limpo = limparCpfCnpj(valor);
  if (/^\d{11}$/.test(limpo)) return "CPF";
  if (/^[0-9A-Z]{12}\d{2}$/.test(limpo)) return "CNPJ";
  return "";
};

const mascararCpf = (valor) => {
  const d = somenteDigitos(valor).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
};

const mascararCnpj = (valor) => {
  const c = limparCnpj(valor);
  let s = c.slice(0, 2);
  if (c.length > 2) s += "." + c.slice(2, 5);
  if (c.length > 5) s += "." + c.slice(5, 8);
  if (c.length > 8) s += "/" + c.slice(8, 12);
  if (c.length > 12) s += "-" + c.slice(12, 14);
  return s;
};

const mascararCpfCnpj = (valor) => (pareceCnpj(valor) ? mascararCnpj(valor) : mascararCpf(valor));

const validarCpf = (valor) => {
  const d = somenteDigitos(valor);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (n) => {
    let soma = 0;
    for (let i = 0; i < n; i += 1) soma += Number(d[i]) * (n + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
};

// Dígito verificador oficial: valor de cada caractere = código ASCII - 48
// (0-9 => 0-9, A => 17 ... Z => 42), pesos 2..9 da direita p/ esquerda, módulo 11.
const digitoCnpj = (base) => {
  const pesos = PESOS_CNPJ.slice(PESOS_CNPJ.length - base.length);
  const soma = base.split("").reduce((acc, c, i) => acc + (c.charCodeAt(0) - 48) * pesos[i], 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
};

const validarCnpj = (valor) => {
  const c = limparCnpj(valor);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(c) || /^(.)\1{13}$/.test(c)) return false;
  const dv1 = digitoCnpj(c.slice(0, 12));
  const dv2 = digitoCnpj(c.slice(0, 12) + dv1);
  return c.slice(12) === `${dv1}${dv2}`;
};

const validarCpfCnpj = (valor) => {
  const tipo = tipoDocumento(valor);
  return tipo === "CPF" ? validarCpf(valor) : tipo === "CNPJ" ? validarCnpj(valor) : false;
};

export {
  somenteDigitos,
  somenteAlfanumericos,
  limparCnpj,
  limparCpfCnpj,
  pareceCnpj,
  tipoDocumento,
  mascararCpf,
  mascararCnpj,
  mascararCpfCnpj,
  validarCpf,
  validarCnpj,
  validarCpfCnpj,
};
